import React from "react";
import * as d3 from "d3";
import { css } from "glamor";
import PropTypes from "prop-types";

import { Axes, Columns } from "@namroodinc/ni-ui";

import SvgBasketballFull from "../../assets/BasketballFull.js";
import SvgBasketballHalf from "../../assets/BasketballHalf.js";
import SvgSoccerFull from "../../assets/SoccerFull.js";
import SvgSoccerHalf from "../../assets/SoccerHalf.js";

const playingAreas = {
  soccer: {
    true: {
      component: SvgSoccerFull,
      width: 850,
      height: 552
    },
    false: {
      component: SvgSoccerHalf,
      width: 424,
      height: 550
    }
  },
  basketball: {
    true: {
      component: SvgBasketballFull,
      width: 964,
      height: 513
    },
    false: {
      component: SvgBasketballHalf,
      width: 483,
      height: 513
    }
  }
};

function PlayingArea({
  fillColor = "green",
  fullPlayingArea = true,
  isLandscape = true,
  margin = 20,
  playingAreaOverlay = null,
  playingAreaUnderlay = null,
  sport = "soccer",
  xDomain = [0, 100],
  yDomain = [0, 100],
  reverseXAndYRange = false
}) {
  const playingArea = playingAreas[sport][fullPlayingArea];

  const height = isLandscape ? playingArea.height : playingArea.width;
  const width = isLandscape ? playingArea.width : playingArea.height;

  const heightWithMargin = height + margin * 2;
  const widthWithMargin = width + margin * 2;

  const rotation = isLandscape
    ? `rotate(0) translate(${margin} ${margin})`
    : `rotate(90) translate(${margin} ${-playingArea.height - margin})`;

  const playingAreaStyle = css({
    " & #playing-area-base": {
      stroke: "white",
      strokeWidth: "2px"
    }
  }).toString();

  let xRange = [0, isLandscape ? playingArea.width : playingArea.height];
  let yRange = [isLandscape ? playingArea.height : playingArea.width, 0];

  if (reverseXAndYRange) {
    xRange = xRange.reverse();
    yRange = yRange.reverse();
  }

  let x = d3
    .scaleLinear()
    .domain(isLandscape ? xDomain : yDomain)
    .range(xRange);

  let y = d3
    .scaleLinear()
    .domain(isLandscape ? yDomain : xDomain?.slice().reverse())
    .range(yRange);

  const callbackProps = {
    height,
    isLandscape,
    width,
    x,
    y
  };

  return (
    <Columns numberOfColumns={1}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox={`0 0 ${widthWithMargin} ${heightWithMargin}`}
        xmlnsXlink="http://www.w3.org/1999/xlink"
      >
        <defs>
          <mask id="landscapeMask">
            <rect x={0} y={0} width={width} height={height} fill="#ffffff" />
          </mask>
          <mask id="portraitMask">
            <rect x={0} y={0} width={height} height={width} fill="#ffffff" />
          </mask>
          <marker
            id={`arrowhead`}
            viewBox="0 0 20 20"
            refX="5"
            refY="5"
            markerWidth="8"
            markerHeight="8"
            orient="auto-start-reverse"
            fill="currentColor"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" />
          </marker>
        </defs>
        <g id="playing-area-background">
          <rect
            x={0}
            y={0}
            width={widthWithMargin}
            height={heightWithMargin}
            fill={fillColor}
          />
        </g>
        {playingAreaUnderlay && (
          <g
            id="playing-area-underlay"
            mask={isLandscape ? "url(#landscapeMask)" : "url(#portraitMask)"}
            transform={`translate(${margin} ${margin})`}
          >
            {playingAreaUnderlay(callbackProps)}
          </g>
        )}
        <g id="playing-area-component" transform={rotation}>
          <playingArea.component className={playingAreaStyle} />
        </g>
        <g id="playing-area-axes">
          <Axes
            xScale={x}
            yScale={y}
            margin={{
              top: margin,
              right: margin,
              bottom: margin,
              left: margin
            }}
            width={width}
            height={height}
            xTicks={10}
            yTicks={10}
          />
        </g>
        {playingAreaOverlay && (
          <g
            id="playing-area-overlay"
            transform={`translate(${margin} ${margin})`}
          >
            {playingAreaOverlay(callbackProps)}
          </g>
        )}
      </svg>
    </Columns>
  );
}

PlayingArea.propTypes = {
  fillColour: PropTypes.string,
  fullPlayingArea: PropTypes.bool,
  isLandscape: PropTypes.bool,
  margin: PropTypes.number,
  playingAreaOverlay: PropTypes.element,
  playingAreaUnderlay: PropTypes.func,
  sport: PropTypes.string,
  xDomain: PropTypes.array,
  yDomain: PropTypes.array
};

export default PlayingArea;
